import { Checkbox, Dialog, DialogContent, DialogActions } from "@mui/material";
import VuiTooltip from "components/VuiTooltip";
import { FaCheck, FaTrashAlt } from "react-icons/fa";
import { useState } from "react";
import dayjs from "utils/dayjs";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import { FaClock, FaUser, FaPen } from "react-icons/fa";
import useCommon from "hooks/useCommon";
import usePermissions from "hooks/usePermissions";
import { GuestProfileName } from "components/GuestProfileDrawer";

import colors from "assets/theme/base/colors";
import rgba from "assets/theme/functions/rgba";

const statusColors = {
  Pending: "#f59e0b",
  "In Progress": "#3b82f6",
  Completed: "#22c55e",
};

const { primary, dark, gradients, light } = colors;

const RequestCard = ({ request, onView, onUpdate, onDelete, onToggleComplete }) => {
  const { STORE } = useCommon();
  const { can } = usePermissions();

  const { bulkCompleteLoading, deleteLoading } = STORE((state) => state.requests);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const isCompleted = request.status === "Completed";

  const formatted = request.dueDate
    ? request.dueTime
      ? dayjs.tz(`${request.dueDate.slice(0, 10)}T${request.dueTime}`).format("MMM D, h:mm A")
      : dayjs.tz(request.dueDate).format("MMM D")
    : "—";
  const isOverdue = request.deadline && dayjs.tz(request.deadline) < dayjs() && !isCompleted;

  return (
    <VuiBox
      p={2.5}
      borderRadius="20px"
      display="flex"
      flexDirection="column"
      sx={{
        backdropFilter: "blur(20px)",
        border: `1px solid ${rgba(dark.main, 0.5)}`,
        transition: "all 0.25s ease",
        cursor: "pointer",
        "&:hover": {
          border: `1px solid ${rgba(primary.main, 0.4)}`,
          transform: "translateY(-4px)",
        },
      }}
    >
      <VuiBox display="flex" justifyContent="space-between" alignItems="flex-start">
        <VuiBox display="flex" flexDirection="column" gap={0.5}>
          {/* Room Name */}
          <VuiTypography variant="button" fontWeight="bold">
            {request.room}
          </VuiTypography>

          {/* Guest Name */}
        <GuestProfileName guestId={request.guestId} hotelId={request.hotelId} name={request.guestName} sx={{ fontSize: 12 }} />
        </VuiBox>

        {/* Checkbox */}
        {can("editRequests") && (
          <VuiTooltip title={isCompleted ? "Completed" : "Mark as completed"}>
            <Checkbox
              checked={isCompleted}
              onClick={(e) => {
                e.stopPropagation();
                if (!isCompleted) {
                  setConfirmOpen(true);
                }
              }}
              sx={{
                color: "#9ca3af",
                "&.Mui-checked": {
                  color: "#22c55e",
                },
              }}
            />
          </VuiTooltip>
        )}
      </VuiBox>

      <VuiTypography
        variant="button"
        fontWeight="medium"
        sx={{
          mt: 2,
          lineHeight: 1.3,
        }}
      >
        {request.title}
      </VuiTypography>

      <VuiTypography
        variant="caption"
        sx={{
          wordBreak: "break-word",
          overflowWrap: "break-word",
          whiteSpace: "pre-line",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          mt: 0.8,
        }}
      >
        {request.description}
      </VuiTypography>

      <VuiBox
        my={3}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={1}
      >
        <VuiTypography
          variant="caption"
          sx={{
            color: statusColors[request.status],
            fontWeight: 500,
          }}
        >
          {request.status}
        </VuiTypography>

        {request.deadline && (
          <VuiBox display="flex" alignItems="center" gap={0.6}>
            <FaClock size={12} color={isOverdue ? "#ef4444" : primary.main} />
            <VuiTypography variant="caption" sx={{ color: isOverdue ? "#ef4444" : primary.main }}>
              {formatted}
            </VuiTypography>
          </VuiBox>
        )}
      </VuiBox>

      {request.assignedTo && (
        <VuiBox display="flex" alignItems="center" gap={0.6} mb={2}>
          <VuiBox
            display="flex"
            alignItems="center"
            justifyContent="center"
            width={18}
            height={18}
            borderRadius="50%"
            sx={{
              background: light.secondary,
            }}
          >
            <FaUser size={8} color={primary.main} />
          </VuiBox>

          <VuiTypography variant="caption">
            {request.assignedTo?.length > 0
              ? `Assigned to ${request.assignedTo.map((u) => u.name).join(", ")}`
              : "Unassigned"}
          </VuiTypography>
        </VuiBox>
      )}

      {/* ===== Actions ===== */}
      <VuiBox mt="auto" display="flex" gap={1}>
        {can("deleteRequests") && (
          <VuiTooltip title="Delete request">
            <VuiButton
              variant="outlined"
              color="error"
              size="small"
              sx={{
                minWidth: "36px",
                width: "36px",
                height: "36px",
                padding: 0,
                borderRadius: "10px",
                border: `1px solid ${rgba(dark.main, 0.5)}`,
                color: primary.main,

                "&:hover": {
                  color: "#ef4444",
                  background: "rgba(239,68,68,0.12)",
                  border: "1px solid rgba(239,68,68,0.35)",
                },
              }}
              onClick={(e) => {
                e.stopPropagation();
                setDeleteConfirmOpen(true);
              }}
            >
              <FaTrashAlt size={12} />
            </VuiButton>
          </VuiTooltip>
        )}

        <VuiButton
          variant="outlined"
          size="small"
          color="primary"
          fullWidth
          onClick={() => onView?.(request)}
        >
          Details
        </VuiButton>

        {can("editRequests") && (
          <VuiButton
            variant="contained"
            color="primary"
            size="small"
            fullWidth
            onClick={() => onUpdate?.(request)}
          >
            Update
          </VuiButton>
        )}
      </VuiBox>

      {/* ===== Confirm Complete Dialog ===== */}
      <Dialog
        open={confirmOpen}
        onClose={(e, reason) => {
          if (bulkCompleteLoading) return;
          if (reason === "backdropClick") return;
          setConfirmOpen(false);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent>
          <VuiBox textAlign="center" py={1}>
            <VuiBox display="flex" alignItems="center" justifyContent="center" gap={1} mb={1}>
              <FaCheck size={18} color="#22c55e" />

              <VuiTypography variant="button" fontWeight="bold">
                Mark request as completed?
              </VuiTypography>
            </VuiBox>

            <VuiTypography variant="caption" mt={1} display="block">
              This action will update the request status to completed.
            </VuiTypography>
          </VuiBox>
        </DialogContent>

        <DialogActions>
          <VuiBox display="flex" justifyContent="center" gap={1} width="100%" px={2} pb={2}>
            <VuiButton
              variant="outlined"
              color="primary"
              disabled={bulkCompleteLoading}
              onClick={() => setConfirmOpen(false)}
            >
              Cancel
            </VuiButton>

            <VuiButton
              variant="contained"
              color="success"
              startIcon={!bulkCompleteLoading && <FaCheck size={12} />}
              disabled={bulkCompleteLoading}
              onClick={async () => {
                await onToggleComplete?.(request);
                setConfirmOpen(false);
              }}
            >
              {bulkCompleteLoading ? "Processing..." : "Confirm"}
            </VuiButton>
          </VuiBox>
        </DialogActions>
      </Dialog>

      {/* ===== Confirm Delete Dialog ===== */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={(e, reason) => {
          if (deleteLoading) return;
          if (reason === "backdropClick") return;
          setDeleteConfirmOpen(false);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent>
          <VuiBox textAlign="center">
            <VuiBox display="flex" alignItems="center" justifyContent="center" gap={1}>
              <FaTrashAlt size={18} color="#ef4444" />

              <VuiTypography variant="button" fontWeight="bold">
                Delete this request?
              </VuiTypography>
            </VuiBox>

            <VuiTypography variant="caption">This action cannot be undone.</VuiTypography>
          </VuiBox>
        </DialogContent>

        <DialogActions>
          <VuiBox display="flex" justifyContent="center" gap={1} width="100%" px={2} pb={2}>
            <VuiButton
              variant="outlined"
              color="primary"
              disabled={deleteLoading}
              onClick={() => setDeleteConfirmOpen(false)}
            >
              Cancel
            </VuiButton>

            <VuiButton
              variant="contained"
              color="error"
              disabled={deleteLoading}
              onClick={async () => {
                const success = await onDelete?.(request);

                if (success) {
                  setDeleteConfirmOpen(false);
                }
              }}
            >
              {deleteLoading ? "Deleting..." : "Delete"}
            </VuiButton>
          </VuiBox>
        </DialogActions>
      </Dialog>
    </VuiBox>
  );
};

export default RequestCard;
